import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import '../caria.css';
import {
  BottomNav,
  BUYER_NAV,
  ColorRail,
  DecisionChip,
  TasadorChip,
  TopBar,
  VehicleCard,
} from '../../../commodity/components/caria';
import { DecisionVerdict } from '../../../commodity/design-tokens';
import { fetchCatalogFeed, CatalogFeedParams } from '../api/catalog.api';
import { FilterBar, VehicleFilters } from '../components/FilterBar';
import { VehicleSummary } from '../types';
import { useAuthChrome } from '../../../core/identity-access/hooks/useAuthChrome';

/**
 * CatalogFeed (T0 Epic A.3, v3 fidelity) — connected buyer discovery screen.
 *
 * Renders the W01 prototype look (TopBar + ColorRail + the v3 VehicleCard
 * cartilla) driven by the live `GET /catalog` feed. Mobile-first. Filters live
 * behind a collapsible toggle so the default view matches the prototype.
 */
const toParams = (f: VehicleFilters): CatalogFeedParams => {
  const year = f.year ? Number(f.year) : undefined;
  return {
    make: f.make?.trim() || undefined,
    model: f.model?.trim() || undefined,
    maxPrice: f.maxPrice ? Number(f.maxPrice) : undefined,
    minYear: year,
    maxYear: year,
    sort: 'newest',
    limit: 12,
  };
};

// Demo image fallback: seeded photos use the stub storage ACL (non-rendering
// URLs), so show a curated stock car photo until real media lands.
const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800&h=600&fit=crop&q=80',
  'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=800&h=600&fit=crop&q=80',
  'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&h=600&fit=crop&q=80',
  'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=800&h=600&fit=crop&q=80',
  'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&h=600&fit=crop&q=80',
];

const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const rd = (n: number) => `RD$ ${Math.round(n * 58.5).toLocaleString('en-US')}`;
const km = (n: number) => n.toLocaleString('en-US');

const heroImage = (v: VehicleSummary, i: number): string => {
  if (v.heroImageUrl && !v.heroImageUrl.includes('X-Amz-Stub')) return v.heroImageUrl;
  return FALLBACK_IMAGES[i % FALLBACK_IMAGES.length];
};

const verdictOf = (seller: number, suggested?: number): DecisionVerdict | undefined => {
  if (!suggested) return undefined;
  const delta = (seller - suggested) / suggested;
  if (delta <= -0.03) return 'good-deal';
  if (delta >= 0.03) return 'overpriced';
  return 'fair-deal';
};

/** Granted trust signals as a plain count — see DEF-005, not a percentage. */
const trustSignals = (marks?: { on: boolean }[]): string => {
  if (!marks || marks.length === 0) return '—';
  return `${marks.filter((m) => m.on).length}/${marks.length}`;
};

export const CatalogFeed: React.FC = () => {
  const authChrome = useAuthChrome();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<VehicleFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [vehicles, setVehicles] = useState<VehicleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const debounce = useRef<ReturnType<typeof setTimeout>>();

  const load = useCallback(async (f: VehicleFilters) => {
    setLoading(true);
    setError(undefined);
    try {
      const { vehicles: rows } = await fetchCatalogFeed(toParams(f));
      const region = f.region?.trim().toLowerCase();
      setVehicles(region ? rows.filter((v) => v.region?.toLowerCase().includes(region)) : rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el catálogo.');
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(filters), 300);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [filters, load]);

  const open = (id: string) => navigate(`/vehicles/${id}`);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surf-page)', paddingBottom: 80 }}>
      <TopBar {...authChrome} nav={BUYER_NAV} />
      <ColorRail />

      <div className="caria-feed-filters">
        <button
          type="button"
          className="caria-feed-filters__toggle"
          onClick={() => setShowFilters((s) => !s)}
          aria-expanded={showFilters}
        >
          {showFilters ? 'Ocultar filtros' : 'Filtros'}
        </button>
        {showFilters && <FilterBar value={filters} onChange={setFilters} />}
      </div>

      <main className="cia-feed" data-testid="vehicle-feed">
        {loading && <p className="caria-feed__status">Cargando vehículos…</p>}
        {!loading && error && (
          <p className="caria-feed__status" role="alert" data-testid="vehicle-feed-error">
            {error}
          </p>
        )}
        {!loading && !error && vehicles.length === 0 && (
          <p className="caria-feed__status" data-testid="results-empty">
            No hay vehículos disponibles.
          </p>
        )}
        {!loading &&
          !error &&
          vehicles.map((v, i) => {
            const verdict = verdictOf(v.sellerPrice, v.suggestedPrice);
            return (
              <VehicleCard
                key={v.id}
                title={`${v.make} ${v.model}`}
                subtitle={`${v.year}`}
                priceUsd={usd(v.sellerPrice)}
                priceSecondary={rd(v.sellerPrice)}
                imageUrl={heroImage(v, i)}
                priceChips={
                  <>
                    {v.suggestedPrice != null && <TasadorChip price={usd(v.suggestedPrice)} />}
                    {verdict && <DecisionChip verdict={verdict} />}
                  </>
                }
                spec={{ year: String(v.year), mileageKm: km(v.mileageKm), location: v.region }}
                trustMarks={v.trustMarks}
                trustSignals={trustSignals(v.trustMarks)}
                onOpen={() => open(v.id)}
                onContact={() => open(v.id)}
                onMessage={() => open(v.id)}
              />
            );
          })}
      </main>
      <BottomNav />
    </div>
  );
};

export default CatalogFeed;
